import { defineStore } from 'pinia'
import api from '@/util/api'
import { Ref, computed, ref } from 'vue'
import { Snap, SnapResult, StartSnapResponse } from '@/types/Snap'

const REFRESH_RESULT_DURATION = 2 * 1000

export const useSnapStore = defineStore('snap', () => {
	const snaps = ref<{
		[snapID: string]: Snap | null
	}>({})

	const assetToSnap = ref<{
		[assetID: string]: string
	}>({})

	const results = ref<{
		[id: string]: SnapResult | null
	}>({})

	const intervals = ref<{
		[result: string]: number
	}>({})

	async function load(id: string): Promise<Snap | null> {
		const res = await api.getSnap(id)
		snaps.value[res.id] = res
		assetToSnap.value[res.asset_id] = res.id
		return res
	}

	async function loadForAsset(assetId: string): Promise<Snap | null> {
		const res = await api.getSnapByAsset(assetId)
		snaps.value[res.id] = res
		assetToSnap.value[res.asset_id] = res.id
		return res
	}

	async function execute(snapId: string, tokens: number): Promise<StartSnapResponse | null> {
		return await api.executeSnap(snapId, tokens)
	}

	async function loadResult(id: string): Promise<SnapResult | null> {
		const res = await api.getSnapResult(id)
		results.value[id] = res
		return res
	}

	function watch(resultID: string): void {
		if (intervals.value[resultID] !== undefined) {
			clearInterval(intervals.value[resultID])
		}

		intervals.value[resultID] = setInterval(() => loadResult(resultID), REFRESH_RESULT_DURATION)
	}

	function unwatch(resultID: string): void {
		if (intervals.value[resultID] !== undefined) {
			clearInterval(intervals.value[resultID])
			delete intervals.value[resultID]
		}
	}

	function unwatchAll(): void {
		for (const k in intervals.value) {
			unwatch(k)
		}
	}

	return {
		snaps,
		assetToSnap,
		results,

		execute,
		load,
		loadForAsset,
		loadResult,
		unwatch,
		unwatchAll,
		watch,
	}
})
